import { Box, Flex, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { pdf } from '@react-pdf/renderer'
import { useEffect, useState } from 'react'
import { FaFilePdf, FaLock, FaTrash } from 'react-icons/fa'
import { MdExpandLess, MdExpandMore } from 'react-icons/md'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import { CriterioResponse, DescricaoCargo, iAssinates, iAssinaturas } from '../../../../../interfaces'
import { appApi } from '../../../../../services/appApi'
import { assinaturasParaPdf } from '../../../utils/assinaturas'
import { formatarDataHora } from '../../../utils/data'
import DescricaoCargoPDF from '../RelatorioPDF'
import { TagStatus } from '../TagStatus'

interface iProps {
  cargoId: string
  onFechou: () => void
}

export interface iVersaoResumo {
  id: string
  ano: number
  sequencia: number
  nome?: string | null
  dataFechamento: string
  fechadoPorUsuarioId: string
}

export interface iSnapshotVersao extends Omit<DescricaoCargo, 'id' | 'solicitacaoReajustes'> {
  criterios: CriterioResponse[]
  assinaturas: iAssinaturas[]
}

interface iVersaoCompleta {
  id: string
  snapshot: iSnapshotVersao
}

const CARDS_VISIVEIS = 2

export function rotuloDaVersao (versao: iVersaoResumo): string {
  if (versao.nome) return versao.nome

  return versao.sequencia > 1 ? `${versao.ano} (${versao.sequencia})` : `${versao.ano}`
}

export async function baixarPdfVersao (cargoId: string, versao: iVersaoResumo, snapshot: iSnapshotVersao, assinante?: iAssinates): Promise<void> {
  const cargo: DescricaoCargo = { ...snapshot, id: cargoId, solicitacaoReajustes: [] }
  const blob = await pdf(
    <DescricaoCargoPDF
      cargo={cargo}
      criterios={snapshot.criterios}
      assinaturas={assinaturasParaPdf(snapshot.assinaturas, assinante)}
    />
  ).toBlob()

  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  const sufixo = assinante ? `_${assinante.nome.replaceAll(' ', '_')}` : ''
  link.download = `Descricao_Cargo_e_Funcao_${snapshot.nomeCargo.replaceAll(' ', '_')}_${rotuloDaVersao(versao).replaceAll(' ', '_')}${sufixo}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

export const HistoricoVersoes: React.FC<iProps> = ({ cargoId, onFechou }) => {
  const toast = useToast()
  const modalNome = useDisclosure()
  const modalExcluir = useDisclosure()
  const [nomeVersao, setNomeVersao] = useState('')
  const [versoes, setVersoes] = useState<iVersaoResumo[]>([])
  const [expandido, setExpandido] = useState(false)
  const [versaoAbertaId, setVersaoAbertaId] = useState<string>()
  const [snapshots, setSnapshots] = useState<Record<string, iVersaoCompleta>>({})
  const [fechando, setFechando] = useState(false)
  const [baixando, setBaixando] = useState<string>()
  const [semAcesso, setSemAcesso] = useState(false)
  const [versaoParaExcluir, setVersaoParaExcluir] = useState<iVersaoResumo>()
  const [excluindo, setExcluindo] = useState(false)
  function carregarVersoes (): void {
    appApi.get(`api/CargosESalarios/HistoricoVersoes/${cargoId}`)
      .then(res => setVersoes(res.data))
      .catch(err => {
        if (err?.response?.status === 403) setSemAcesso(true)
        console.log(err)
      })
  }

  function abrirModalNome (): void {
    setFechando(true)

    appApi.get(`api/CargosESalarios/FecharVersao/Validar/${cargoId}`)
      .then(() => modalNome.onOpen())
      .catch(err => {
        const mensagem = err?.response?.data?.mensagem
        if (err?.response?.status === 400 && mensagem) {
          toast({ title: mensagem, duration: 6000, status: 'warning', position: 'top' })
        } else {
          console.log(err)
        }
      })
      .finally(() => setFechando(false))
  }

  function fecharVersao (): void {
    modalNome.onClose()
    setFechando(true)

    appApi.post('api/CargosESalarios/FecharVersao', { CargoId: cargoId, Nome: nomeVersao.trim() === '' ? undefined : nomeVersao.trim() })
      .then(() => {
        toast({ title: 'Versão fechada com sucesso', duration: 4000, status: 'success', position: 'top' })
        carregarVersoes()
        onFechou()
      })
      .catch(err => {
        const mensagem = err?.response?.data?.mensagem
        if (err?.response?.status === 400 && mensagem) {
          toast({ title: mensagem, duration: 6000, status: 'warning', position: 'top' })
        } else {
          console.log(err)
        }
      })
      .finally(() => {
        setFechando(false)
        setNomeVersao('')
      })
  }

  function abrirExclusao (versao: iVersaoResumo): void {
    setVersaoParaExcluir(versao)
    modalExcluir.onOpen()
  }

  function excluirVersao (): void {
    if (!versaoParaExcluir) return

    setExcluindo(true)
    appApi.delete(`api/CargosESalarios/HistoricoVersoes/${cargoId}/${versaoParaExcluir.id}`)
      .then(() => {
        toast({ title: 'Versão excluída', duration: 4000, status: 'success', position: 'top' })
        modalExcluir.onClose()
        carregarVersoes()
      })
      .catch(err => console.log(err))
      .finally(() => setExcluindo(false))
  }

  // O snapshot completo so e buscado quando a versao e aberta ou baixada, nunca na lista
  async function obterSnapshot (versaoId: string): Promise<iVersaoCompleta> {
    if (snapshots[versaoId]) return snapshots[versaoId]

    const res = await appApi.get(`api/CargosESalarios/HistoricoVersoes/${cargoId}/${versaoId}`)
    setSnapshots(prev => ({ ...prev, [versaoId]: res.data }))

    return res.data
  }

  function alternarVersao (versaoId: string): void {
    if (versaoAbertaId === versaoId) {
      setVersaoAbertaId(undefined)
      return
    }

    setVersaoAbertaId(versaoId)
    obterSnapshot(versaoId).catch(err => console.log(err))
  }

  function baixarPdf (versao: iVersaoResumo, assinante?: iAssinates): void {
    setBaixando(`${versao.id}:${assinante?.email ?? ''}`)

    obterSnapshot(versao.id)
      .then(async ({ snapshot }) => await baixarPdfVersao(cargoId, versao, snapshot, assinante))
      .catch(err => console.log(err))
      .finally(() => setBaixando(undefined))
  }

  useEffect(() => {
    carregarVersoes()
  }, [cargoId])

  if (semAcesso) return null

  const visiveis = expandido ? versoes : versoes.slice(0, CARDS_VISIVEIS)
  const ocultos = versoes.length - CARDS_VISIVEIS

  return (
    <Flex
      flexDir={'column'}
      gap={'1.25rem'}
      w={'100%'}
      border={'1px solid var(--Gray5)'}
      borderRadius={'.375rem'}
      boxShadow={'var(--SombraBackground)'}
      p={'1.25rem'}
      mt={'1rem'}
    >
      <Modal isOpen={modalNome.isOpen} onClose={modalNome.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={'blue.400'}>Fechar versão</ModalHeader>
          <ModalBody>
            <Text fontSize={'14px'} mb={'.5rem'}>Nome da versão (opcional). Se ficar em branco, será usado o ano de fechamento.</Text>
            <Input value={nomeVersao} onChange={e => setNomeVersao(e.target.value)} maxLength={100} placeholder={'Ex: Revisão anual de 2027'} />
          </ModalBody>
          <ModalFooter gap={'.75rem'}>
            <ButtonCmp OutlineColor={'c6'} onClick={modalNome.onClose}>Cancelar</ButtonCmp>
            <ButtonCmp VarColor={'Green2'} onClick={fecharVersao}>Confirmar</ButtonCmp>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={modalExcluir.isOpen} onClose={modalExcluir.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={'var(--Red)'}>Excluir versão {versaoParaExcluir ? rotuloDaVersao(versaoParaExcluir) : ''}</ModalHeader>
          <ModalBody>
            <Text fontSize={'14px'} color={'var(--Gray1)'}>
              Essa ação não pode ser desfeita. Ao excluir, todas as informações e assinaturas guardadas nessa versão
              serão perdidas e não poderão ser recuperadas, e o cargo atual pode não refletir mais o que está (ou estava)
              registrado no sistema.
            </Text>
          </ModalBody>
          <ModalFooter gap={'.75rem'}>
            <ButtonCmp OutlineColor={'c6'} onClick={modalExcluir.onClose}>Cancelar</ButtonCmp>
            <ButtonCmp VarColor={'Red'} isLoading={excluindo} onClick={excluirVersao}>Excluir definitivamente</ButtonCmp>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex justifyContent={'space-between'} alignItems={'flex-start'} gap={'1rem'} flexWrap={'wrap'}>
        <Box>
          <Text fontSize={'18px'} fontWeight={'600'} color={'var(--Gray1)'}>Versões fechadas</Text>
          <Text fontSize={'13px'} color={'var(--Gray3)'} mt={'.125rem'}>Cópias somente leitura do cargo no momento em que todos assinaram</Text>
        </Box>
        <ButtonCmp VarColor={'Green2'} leftIcon={<FaLock />} isLoading={fechando} onClick={abrirModalNome}>
          Fechar versão
        </ButtonCmp>
      </Flex>

      {versoes.length === 0
        ? (
          <Box border={'1px dashed var(--Gray5)'} borderRadius={'.375rem'} p={'1.5rem'} textAlign={'center'}>
            <Text fontSize={'14px'} color={'var(--Gray3)'}>Nenhuma versão fechada para este cargo.</Text>
          </Box>
          )
        : (
          <Flex flexDir={'column'} gap={'1rem'}>
            {visiveis.map(versao => {
              const aberta = versaoAbertaId === versao.id
              const snapshot = snapshots[versao.id]?.snapshot

              return (
                <Box key={versao.id} border={'1px solid var(--Gray5)'} borderRadius={'.375rem'} boxShadow={'var(--SombraBackground)'} overflow={'hidden'}>
                  <Flex p={'1rem'} justifyContent={'space-between'} alignItems={'center'} gap={'1rem'} flexWrap={'wrap'}>
                    <Box>
                      <Text fontSize={'15px'} fontWeight={'500'} color={'var(--Gray1)'}>Versão {rotuloDaVersao(versao)}</Text>
                      <Text fontSize={'12px'} color={'var(--Gray4)'} mt={'.125rem'}>
                        Fechada em {formatarDataHora(versao.dataFechamento)}
                      </Text>
                    </Box>
                    <Flex gap={'.5rem'} flexWrap={'wrap'}>
                      <ButtonCmp
                        small
                        OutlineColor={'Green2'}
                        leftIcon={<FaFilePdf />}
                        isLoading={baixando === `${versao.id}:`}
                        onClick={() => baixarPdf(versao)}
                      >
                        Baixar PDF
                      </ButtonCmp>
                      <ButtonCmp
                        small
                        OutlineColor={'c7'}
                        leftIcon={aberta ? <MdExpandLess /> : <MdExpandMore />}
                        onClick={() => alternarVersao(versao.id)}
                      >
                        {aberta ? 'Ocultar assinantes' : 'Ver assinantes'}
                      </ButtonCmp>
                      <ButtonCmp
                        small
                        OutlineColor={'Red'}
                        leftIcon={<FaTrash />}
                        onClick={() => abrirExclusao(versao)}
                      >
                        Excluir
                      </ButtonCmp>
                    </Flex>
                  </Flex>

                  {aberta && (
                    <Flex flexDir={'column'} borderTop={'1px solid var(--Gray5)'}>
                      {snapshot === undefined
                        ? <Text p={'1rem'} fontSize={'13px'} color={'var(--Gray3)'}>Carregando...</Text>
                        : (snapshot.assinantes ?? []).map(assinante => {
                            const assinatura = snapshot.assinaturas.find(a => a.emailAssinante === assinante.email && a.assinado)

                            return (
                              <Flex key={assinante.email} p={'1rem'} justifyContent={'space-between'} alignItems={'center'} gap={'1rem'} flexWrap={'wrap'} borderBottom={'1px solid var(--Gray5)'} _last={{ borderBottom: 'none' }}>
                                <Box>
                                  <Text fontSize={'14px'} fontWeight={'500'} color={'var(--Gray1)'}>{assinante.nome}</Text>
                                  <Text fontSize={'13px'} color={'var(--Gray3)'}>{assinante.email}</Text>
                                  {assinatura?.assinadoEm && (
                                    <Text fontSize={'12px'} color={'var(--Gray4)'}>Assinou em {formatarDataHora(assinatura.assinadoEm)}</Text>
                                  )}
                                </Box>
                                <Flex alignItems={'center'} gap={'.75rem'}>
                                  <TagStatus status={assinante.status ?? 0} />
                                  <ButtonCmp
                                    small
                                    OutlineColor={'Green2'}
                                    leftIcon={<FaFilePdf />}
                                    isLoading={baixando === `${versao.id}:${assinante.email}`}
                                    onClick={() => baixarPdf(versao, assinante)}
                                  >
                                    Baixar PDF
                                  </ButtonCmp>
                                </Flex>
                              </Flex>
                            )
                          })}
                    </Flex>
                  )}
                </Box>
              )
            })}
          </Flex>
          )}

      {ocultos > 0 && (
        <Flex justifyContent={'center'}>
          <ButtonCmp
            OutlineColor={'Rosa'}
            leftIcon={expandido ? <MdExpandLess /> : <MdExpandMore />}
            onClick={() => setExpandido(!expandido)}
          >
            {expandido ? 'Ocultar versões' : `Ver todas as versões (${ocultos})`}
          </ButtonCmp>
        </Flex>
      )}
    </Flex>
  )
}
